import React, { useEffect, useState } from 'react';
import { ConnectWebSocketForScreenV2, DisconnectWebSocket, SetSocketMessageHandler, ClearSocketMessageHandler } from './../../utilities/BL/Zones';

// Importa tus componentes (Juegos y Overlays)
import { GoalScreenV2 } from './Sections/GoalScreenV2';
import { FrontPageRunToGoal } from './Sections/Goal/Brands/Daikin/FrontPageRunToGoal';
import { LuchoFest } from './Sections/LuchoFest';
import { SelfieFan } from './Sections/SelfieFan';
import { InteractiveChat } from './../Overlays/InteractiveChat'; // Ejemplo
import { PlaylistScreen } from './Sections/PlaylistScreen';
import { TugOfWarCover } from './Sections/TugOfWar/TugOfWarCover';
import { TugOfWarGame } from './Sections/TugOfWar/TugOfWarGame';
import { MemoryCover, MemoryGame } from './Sections/Memory';


const ScreenV2 = () => {

    // Definimos qué pantalla somos (Podría venir de la URL, ej: ?screenId=1)
    const screenId = 1; 

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [toastMessage, setToastMessage] = useState('');

    // ==========================================================================
    // ESTADO GLOBAL DEL LIENZO (El Cerebro)
    // ==========================================================================
    const [screenState, setScreenState] = useState({
        activeMainDynamic: null, // El juego a pantalla completa
        overlays: {}             // Diccionario de widgets flotantes
    });

    // ==========================================================================
    // PROCESADOR DE WEBSOCKETS (Escuchando al Backend V2)
    // ==========================================================================
    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        switch (message.type.toLowerCase()) {
            case 'screenv2.sync':
                // Recibimos la Fotografía exacta desde el Singleton de C#
                if (message.payload) {
                    setScreenState({
                        activeMainDynamic: message.payload.activeMainDynamic,
                        overlays: message.payload.overlays || {}
                    });
                }
                break;

            case 'playlist.project':
            case 'playlist.screen':
            case 'playlist.prepare':
                setScreenState(prev => ({
                    ...prev,
                    activeMainDynamic: 'Playlist'
                }));
                break;

            case 'tugofwar.cover':
            case 'tugofwar_show_cover':
                setScreenState(prev => ({
                    ...prev,
                    activeMainDynamic: 'TugOfWarCover'
                }));
                break;

            case 'tugofwar.prepare':
            case 'tugofwar_show_game':
            case 'tugofwar_round_started':
                setScreenState(prev => ({
                    ...prev,
                    activeMainDynamic: 'TugOfWar'
                }));
                break;

            case 'memory.cover':
            case 'memory_show_cover':
            case 'memory_cover_shown':
                setScreenState(prev => ({
                    ...prev,
                    activeMainDynamic: 'MemoryCover'
                }));
                break;

            case 'memory.prepare':
            case 'memory_show_game':
            case 'memory_game_scene_shown':
            case 'memory_game_start':
                setScreenState(prev => ({
                    ...prev,
                    activeMainDynamic: 'MemoryGame'
                }));
                break;

            // Aquí puedes dejar pasar eventos locales de los juegos (ej. 'goal.progress')
            // porque los componentes hijos (GoalScreenV2) aún pueden tener sus propios WebSockets internos,
            // o puedes centralizarlos aquí. Por ahora, solo nos interesa armar el Layout.
            default:
                break;
        }
    };

    useEffect(() => {
        const connect = async () => {
            await ConnectWebSocketForScreenV2(screenId, setConnectionStatus, showToast);
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connect();

        // Fondo transparente base para OBS
        document.body.classList.add('customBg'); 
        document.body.style.backgroundColor = "transparent";

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, []);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(''), 3000);
    };

    // ==========================================================================
    // RENDERIZADO DEL LIENZO (Compositor Broadcast)
    // Usamos position 'absolute' para que todos convivan en el mismo pixel
    // ==========================================================================
    
    // Estilo maestro para que cada capa cubra toda la pantalla sin empujar a las demás
    const layerStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none', // Para que los clics pasen a través de las capas transparentes
        overflow: 'hidden'
    };

    return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
            
            {/* -----------------------------------------------------------
                NIVEL 10: WIDGETS DE FONDO / FIJOS 
                (Ej. El Marcador del partido, un cintillo de noticias)
            ----------------------------------------------------------- */}
            <div style={{ ...layerStyle, zIndex: 10 }}>
                {/* {screenState.overlays['Scoreboard']?.isVisible && <Scoreboard />} */}
            </div>


            {/* -----------------------------------------------------------
                NIVEL 20: OVERLAYS DE INTERACCIÓN 
                (Cosas que flotan con transparencia, ej. Selfies, Mensajes)
            ----------------------------------------------------------- */}
            <div style={{ ...layerStyle, zIndex: 20 }}>
                {/* Asumiendo que tu componente SelfieFan tiene fondo transparente en su CSS */}
                {screenState.overlays['Selfies']?.isVisible && (
                    <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
                        <SelfieFan />
                    </div>
                )}
                
                {screenState.overlays['Chat']?.isVisible && (
                    <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
                        <InteractiveChat />
                    </div>
                )}
            </div>


            {/* -----------------------------------------------------------
                NIVEL 50: DINÁMICAS PRINCIPALES (FULL SCREEN)
                (Juegos pesados que tapan todo el estadio)
            ----------------------------------------------------------- */}
            <div style={{ ...layerStyle, zIndex: 50, pointerEvents: 'auto' }}>
                
                {/* Si el backend dice "RunToGoal", lo inyectamos */}
                {screenState.activeMainDynamic === 'RunToGoal' && (
                    // <GoalScreenV2 currentMatchState={null} /> // Le puedes pasar los props que requiera
                    <FrontPageRunToGoal currentMatchState={null} /> // Le puedes pasar los props que requiera
                )}

                {/* Si el backend dice "LuchoFest", lo inyectamos */}
                {screenState.activeMainDynamic === 'LuchoFest' && (
                    <LuchoFest /> 
                )}

                {/* Si el backend dice "Playlist", lo inyectamos */}
                {screenState.activeMainDynamic === 'Playlist' && (
                    <PlaylistScreen />
                )}

                {/* Si el backend dice "TugOfWarCover", lo inyectamos */}
                {screenState.activeMainDynamic === 'TugOfWarCover' && (
                    <TugOfWarCover />
                )}

                {/* Si el backend dice "TugOfWar", lo inyectamos */}
                {screenState.activeMainDynamic === 'TugOfWar' && (
                    <TugOfWarGame />
                )}

                {/* Si el backend dice "MemoryCover", lo inyectamos */}
                {screenState.activeMainDynamic === 'MemoryCover' && (
                    <MemoryCover />
                )}

                {/* Si el backend dice "MemoryGame" o "Memory", lo inyectamos */}
                {(screenState.activeMainDynamic === 'MemoryGame' || screenState.activeMainDynamic === 'Memory') && (
                    <MemoryGame />
                )}

            </div>



            {/* MENSAJES DE SISTEMA */}
            {toastMessage && (
                <div className="toast-container position-fixed bottom-0 start-50 translate-middle-x mb-3" style={{ zIndex: 9999 }}>
                    <div className="toast show bg-dark text-white px-3 py-2 shadow rounded border-light">
                        {toastMessage}
                    </div>
                </div>
            )}
        </div>
    );
};

export { ScreenV2 };